import { useState, useRef, useCallback, useEffect } from 'react';
import axios from 'axios';

/**
 * Chunked Speech-to-Text hook for Adult Mode.
 *
 * iOS Safari does NOT reliably support continuous WebSocket PCM streaming
 * (AudioContext issues). MediaRecorder, however, works on iOS 14.5+.
 *
 * This hook records audio in rolling N-second chunks. Each chunk is a
 * COMPLETE, decodable audio file (we stop+restart the recorder rather
 * than relying on MediaRecorder.start(timeslice) which can produce
 * non-standalone fragments on Safari).
 *
 * Each chunk is POSTed to /api/speech/transcribe-chunk and the resulting
 * transcript is delivered word-by-word via onTranscript(word).
 */
export function useChunkedSTT({
  onTranscript,
  onChunk,           // optional: receives raw chunk result { transcribed_text, similarity_score }
  onError,
  chunkMs = 4000,    // overridden by parent — 5s in adult mode
  getExpectedText,   // optional: () => string — current expected ayah text for similarity
} = {}) {
  const [isStreaming, setIsStreaming] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);

  const mediaStreamRef = useRef(null);
  const recorderRef = useRef(null);
  const chunkBufferRef = useRef([]);
  const cycleTimerRef = useRef(null);
  const mimeTypeRef = useRef('');
  const stoppingRef = useRef(false);
  const seenWordsRef = useRef(new Set()); // dedupe across overlapping chunks

  const pickMimeType = () => {
    const candidates = [
      'audio/webm;codecs=opus',
      'audio/webm',
      'audio/mp4;codecs=mp4a.40.2',
      'audio/mp4',
      'audio/ogg;codecs=opus',
    ];
    for (const m of candidates) {
      if (typeof MediaRecorder !== 'undefined' && MediaRecorder.isTypeSupported && MediaRecorder.isTypeSupported(m)) {
        return m;
      }
    }
    return '';
  };

  const sendChunkToBackend = useCallback(async (blob) => {
    if (!blob || blob.size < 1024) return; // skip tiny / empty chunks
    try {
      setIsProcessing(true);
      const formData = new FormData();
      const ext = (mimeTypeRef.current || 'audio/webm').includes('mp4') ? 'mp4' : 'webm';
      formData.append('audio', blob, `chunk.${ext}`);
      const expected = typeof getExpectedText === 'function' ? (getExpectedText() || '') : '';
      if (expected) formData.append('expected_text', expected);

      const token = localStorage.getItem('hafizim_token');
      const base = ((typeof window !== 'undefined' && window.__hafizim_backend_url) || process.env.REACT_APP_BACKEND_URL || '');
      const res = await axios.post(`${base}/api/speech/transcribe-chunk`, formData, {
        headers: {
          Authorization: token ? `Bearer ${token}` : undefined,
        },
        timeout: 20000,
      });

      const data = res.data || {};
      if (onChunk) onChunk(data);

      const text = (data.transcribed_text || '').trim();
      if (text && onTranscript) {
        const words = text.split(/\s+/).filter(Boolean);
        for (const w of words) {
          // simple dedupe: avoid replaying the same exact word emitted moments ago
          const key = `${w}|${Date.now() >> 12}`; // bucket per ~4s
          if (seenWordsRef.current.has(w)) continue;
          seenWordsRef.current.add(w);
          // keep set small
          if (seenWordsRef.current.size > 64) {
            const first = seenWordsRef.current.values().next().value;
            seenWordsRef.current.delete(first);
          }
          onTranscript(w);
        }
      }
    } catch (e) {
      console.error('[chunked-stt] upload failed:', e?.message || e);
      if (onError) onError('Ses parçası işlenemedi: ' + (e?.message || e));
    } finally {
      setIsProcessing(false);
    }
  }, [getExpectedText, onChunk, onTranscript, onError]);

  const startCycle = useCallback(() => {
    if (!mediaStreamRef.current || stoppingRef.current) return;
    try {
      chunkBufferRef.current = [];
      const opts = mimeTypeRef.current ? { mimeType: mimeTypeRef.current } : undefined;
      const recorder = new MediaRecorder(mediaStreamRef.current, opts);
      recorderRef.current = recorder;

      recorder.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunkBufferRef.current.push(e.data);
      };

      recorder.onstop = async () => {
        const parts = chunkBufferRef.current;
        chunkBufferRef.current = [];
        const blob = new Blob(parts, { type: mimeTypeRef.current || 'audio/webm' });
        // Fire-and-forget upload so we don't block the next cycle
        sendChunkToBackend(blob);
        if (!stoppingRef.current) {
          // schedule next cycle immediately
          startCycle();
        }
      };

      recorder.start();
      cycleTimerRef.current = setTimeout(() => {
        try {
          if (recorderRef.current && recorderRef.current.state === 'recording') {
            recorderRef.current.stop();
          }
        } catch (err) {
          console.warn('[chunked-stt] stop error:', err);
        }
      }, chunkMs);
    } catch (err) {
      console.error('[chunked-stt] cycle start failed:', err);
      if (onError) onError('Kayıt başlatılamadı: ' + err.message);
    }
  }, [chunkMs, sendChunkToBackend, onError]);

  const startStreaming = useCallback(async () => {
    if (isStreaming) return;
    if (typeof MediaRecorder === 'undefined') {
      if (onError) onError('Bu tarayıcı sesli kayıt desteklemiyor.');
      return;
    }
    try {
      stoppingRef.current = false;
      seenWordsRef.current.clear();
      mimeTypeRef.current = pickMimeType();
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          channelCount: 1,
          echoCancellation: true,
          noiseSuppression: true,
        },
      });
      mediaStreamRef.current = stream;
      setIsStreaming(true);
      startCycle();
    } catch (err) {
      console.error('[chunked-stt] getUserMedia failed:', err);
      if (onError) {
        if (err.name === 'NotAllowedError') onError('Mikrofon izni reddedildi.');
        else if (err.name === 'NotFoundError') onError('Mikrofon bulunamadı.');
        else onError('Mikrofon hatası: ' + err.message);
      }
    }
  }, [isStreaming, onError, startCycle]);

  const stopStreaming = useCallback(() => {
    stoppingRef.current = true;
    if (cycleTimerRef.current) {
      clearTimeout(cycleTimerRef.current);
      cycleTimerRef.current = null;
    }
    try {
      if (recorderRef.current && recorderRef.current.state === 'recording') {
        recorderRef.current.stop();
      }
    } catch (e) { /* noop */ }
    if (mediaStreamRef.current) {
      mediaStreamRef.current.getTracks().forEach(t => t.stop());
      mediaStreamRef.current = null;
    }
    recorderRef.current = null;
    setIsStreaming(false);
  }, []);

  useEffect(() => () => stopStreaming(), [stopStreaming]);

  return {
    isStreaming,
    isProcessing,
    startStreaming,
    stopStreaming,
    isSupported: typeof MediaRecorder !== 'undefined',
  };
}
